class SortDropdown extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select');
    if (!this.select || this.trigger) return;

    this.build();
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.trigger.addEventListener('click', () => (this.isOpen() ? this.close() : this.open()));
    this.trigger.addEventListener('keydown', this.onTriggerKeydown.bind(this));
    this.list.addEventListener('keydown', this.onListKeydown.bind(this));
    this.list.addEventListener('click', (event) => {
      const option = event.target.closest('[role="option"]');
      if (option) this.choose(option);
    });
    this.classList.add('sort-dropdown--ready');
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  build() {
    const listId = `${this.select.id}-listbox`;
    const selected = this.select.options[this.select.selectedIndex];

    this.trigger = document.createElement('button');
    this.trigger.type = 'button';
    this.trigger.className = 'sort-dropdown__trigger';
    this.trigger.setAttribute('aria-haspopup', 'listbox');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.setAttribute('aria-controls', listId);
    const label = document.querySelector(`label[for="${this.select.id}"]`);
    if (label) this.trigger.setAttribute('aria-label', label.textContent.trim());

    this.value = document.createElement('span');
    this.value.className = 'sort-dropdown__value';
    this.value.textContent = selected ? selected.textContent.trim() : '';

    const caret = this.querySelector('.icon-caret');
    this.trigger.append(this.value);
    if (caret) this.trigger.append(caret.cloneNode(true));

    this.list = document.createElement('ul');
    this.list.id = listId;
    this.list.className = 'sort-dropdown__list';
    this.list.setAttribute('role', 'listbox');
    this.list.tabIndex = -1;

    Array.from(this.select.options)
      .filter((option) => !option.disabled)
      .forEach((option) => {
        const item = document.createElement('li');
        item.className = 'sort-dropdown__option';
        item.setAttribute('role', 'option');
        item.dataset.value = option.value;
        item.id = `${listId}-${option.value}`;
        item.setAttribute('aria-selected', option.selected ? 'true' : 'false');
        item.textContent = option.textContent.trim();
        this.list.append(item);
      });

    this.append(this.trigger, this.list);
    this.classList.toggle('sort-dropdown--placeholder', this.select.value === '');
  }

  options() {
    return Array.from(this.list.querySelectorAll('[role="option"]'));
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  open() {
    this.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
    const current = this.list.querySelector('[aria-selected="true"]') || this.options()[0];
    this.setActive(current);
    this.list.focus({ preventScroll: true });
    document.addEventListener('click', this.onDocumentClick);
  }

  close(focusTrigger = false) {
    this.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.list.removeAttribute('aria-activedescendant');
    document.removeEventListener('click', this.onDocumentClick);
    if (focusTrigger) this.trigger.focus();
  }

  setActive(option) {
    if (!option) return;
    this.options().forEach((item) => item.classList.toggle('is-active', item === option));
    this.list.setAttribute('aria-activedescendant', option.id);
  }

  choose(option) {
    const changed = this.select.value !== option.dataset.value;
    this.options().forEach((item) => item.setAttribute('aria-selected', item === option ? 'true' : 'false'));
    this.value.textContent = option.textContent;
    this.classList.remove('sort-dropdown--placeholder');
    this.close(true);
    if (!changed) return;
    this.select.value = option.dataset.value;
    this.select.dispatchEvent(new Event('input', { bubbles: true }));
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  onTriggerKeydown(event) {
    if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      this.open();
    }
  }

  onListKeydown(event) {
    const items = this.options();
    const index = items.findIndex((item) => item.classList.contains('is-active'));
    const moves = {
      ArrowDown: Math.min(index + 1, items.length - 1),
      ArrowUp: Math.max(index - 1, 0),
      Home: 0,
      End: items.length - 1,
    };

    if (event.key in moves) {
      event.preventDefault();
      this.setActive(items[moves[event.key]]);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (items[index]) this.choose(items[index]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close(true);
    } else if (event.key === 'Tab') {
      this.close();
    }
  }
}

if (!customElements.get('sort-dropdown')) customElements.define('sort-dropdown', SortDropdown);
